// ABOUTME: Zod validation schemas and types for TechLog → Rusada defect transformations.
import {
  ZRusadaDefectInput,
  ZRusadaDefectCloseInput,
  ZRusadaDefectDeferInput,
  ZRusadaRegistrationResponse,
  ZRusadaDefectCategory,
  ZRusadaEmployee,
  ZRusadaDefectResponse,
  ZRusadaAtaChapterResponse,
} from '../../../../activities/api/rusada/rusada.zod';
import {
  ZTechlogDefectResponse,
  ZTechlogDefectResolution,
} from '../../../../types/techLog/defect.zod';
import { ZTechlogUser } from '../../../../types/techLog/user.zod';
import { z } from 'zod';

/**
 * Input validation schema for CREATE/UPDATE actions.
 * Uses flat structure: source.objectName and destination.objectName
 */
const ZRusadaSectorLink = z.object({
  techLogId: z.number(),
  sectorId: z.number().optional(),
});

export const ZTechLogToRusadaDefectInput = z.object({
  techlog: z.object({
    defect: ZTechlogDefectResponse,
    reportingUser: ZTechlogUser.optional(),
  }),
  rusada: z.object({
    registration: ZRusadaRegistrationResponse,
    defect: ZRusadaDefectResponse.optional(),
    defectCategories: z.array(ZRusadaDefectCategory).optional(),
    reportingUser: ZRusadaEmployee.optional(),
    sector: ZRusadaSectorLink.optional(),
    ataChapters: z.array(ZRusadaAtaChapterResponse).optional(),
  }),
});

/**
 * Input validation schema for CLOSE action.
 * rusada.defect required (provides defectId).
 * rusada.reportingUser required (API requires carriedOutByEmployeeId).
 */
export const ZTechLogToRusadaDefectCloseInput = z.object({
  techlog: z.object({
    defect: ZTechlogDefectResponse,
    defectResolution: ZTechlogDefectResolution.optional(),
    reportingUser: ZTechlogUser.optional(),
  }),
  rusada: z.object({
    registration: ZRusadaRegistrationResponse,
    defect: ZRusadaDefectResponse,
    reportingUser: ZRusadaEmployee.optional().refine((val) => !!val, {
      message: 'Closing a defect requires a matching Rusada employee.',
    }),
    sector: ZRusadaSectorLink.optional(),
  }),
});

/**
 * Input validation schema for DEFER action.
 * rusada.defect required (provides defectId).
 * rusada.sector required (provides techLogSectorId for deferral) — surfaced
 * via a field-level refine so the customer sees an actionable message instead
 * of a raw Zod path. Vendor terms (`Rusada`, `Techlog`) are written in the
 * canonical form so notification clientization translates them for client
 * recipients while internal recipients see the raw vendor names.
 */
export const ZTechLogToRusadaDefectDeferInput = z.object({
  techlog: z.object({
    defect: ZTechlogDefectResponse,
    deferringUser: ZTechlogUser.optional(),
  }),
  rusada: z.object({
    registration: ZRusadaRegistrationResponse,
    defect: ZRusadaDefectResponse,
    deferringUser: ZRusadaEmployee.optional(),
    sector: ZRusadaSectorLink.optional().refine((val) => !!val, {
      message: 'Defect must be linked to a sector to be deferred.',
    }),
  }),
});

/**
 * Input validation schema for LINK_TO_SECTOR action.
 * Extends base CREATE/UPDATE input but makes sector and its sectorId required —
 * fails fast if sector not synced.
 */
export const ZTechLogToRusadaDefectLinkToSectorInput =
  ZTechLogToRusadaDefectInput.extend({
    rusada: ZTechLogToRusadaDefectInput.shape.rusada.extend({
      defect: ZRusadaDefectResponse,
      sector: ZRusadaSectorLink.required(),
    }),
  });

export const ZTechLogToRusadaDefectOutput = ZRusadaDefectInput;
export const ZTechLogToRusadaDefectCloseOutput = ZRusadaDefectCloseInput;
export const ZTechLogToRusadaDefectDeferOutput = ZRusadaDefectDeferInput;
export const ZTechLogToRusadaDefectLinkToSectorOutput = ZRusadaDefectInput;

export type TechLogToRusadaDefectInput = z.infer<
  typeof ZTechLogToRusadaDefectInput
>;
export type TechLogToRusadaDefectCloseInput = z.infer<
  typeof ZTechLogToRusadaDefectCloseInput
>;
export type TechLogToRusadaDefectDeferInput = z.infer<
  typeof ZTechLogToRusadaDefectDeferInput
>;
export type TechLogToRusadaDefectOutput = z.infer<
  typeof ZTechLogToRusadaDefectOutput
>;
export type TechLogToRusadaDefectCloseOutput = z.infer<
  typeof ZTechLogToRusadaDefectCloseOutput
>;
export type TechLogToRusadaDefectDeferOutput = z.infer<
  typeof ZTechLogToRusadaDefectDeferOutput
>;
export type TechLogToRusadaDefectLinkToSectorInput = z.infer<
  typeof ZTechLogToRusadaDefectLinkToSectorInput
>;
export type TechLogToRusadaDefectLinkToSectorOutput = z.infer<
  typeof ZTechLogToRusadaDefectLinkToSectorOutput
>;
